/* ============================================================================
   os-lp-reveal.js — scroll reveal for the landing page sections
   ----------------------------------------------------------------------------
   Lauren, pointing at moonbrew.co/pages/gummies: "the scroll is dynamic and
   blends into the next thing — this is how the whole page should behave."
   Sections fade and rise as they enter the viewport, so nothing ever abruptly
   appears. (Their sticky product image is the other half; the buy box already
   does that.)

   ---------------------------------------------------------------------------
   THIS FILE HAS ALREADY BROKEN THE PAGE ONCE. READ THIS BEFORE EDITING.

   The 2026-09-09 build shipped and, on the live preview, armed all 38 elements
   and revealed none of them: six sections invisible, including elements
   sitting in the viewport, still hidden 4.4s after a real scroll. Both of its
   supposedly independent safety mechanisms failed at the same time.

   The reason both failed is that both were built on the SAME broken
   assumption: that the element references the script captured at init are
   still the elements on the page. They shared a module-level `armedList` of
   captured nodes, and the observer held those same nodes. Anything that
   replaces or re-renders that markup after init — a theme page-transition, a
   section re-render, an app rewriting a container — detaches every node the
   script is holding. The observer then watches orphans that never intersect;
   the sweep walks orphans, finds them all at top:0, "reveals" them, empties
   the list and unhooks itself; the watchdog reads the now-empty list and
   concludes there is nothing wrong. Meanwhile the live DOM holds 38 clones
   that carry the .os-reveal--armed class and are hidden forever.

   So v3 HOLDS NO NODE REFERENCES AT ALL. Every mechanism re-queries the
   document. If the markup is replaced, the next query finds the new markup.
   The layers, each of which alone is sufficient to un-hide the page:

     1. IntersectionObserver  — the nice path, gives the stagger its timing
     2. scroll/resize sweep   — fresh querySelectorAll, rAF-throttled
     3. interval sweep        — fresh querySelectorAll every 400ms, depends on
                                neither scroll events nor the observer, so
                                nothing the user can SEE stays hidden for more
                                than about 400ms whatever else has failed
     4. hard failsafe         — a plain setTimeout that unconditionally reveals
                                everything still armed at 8s, position and
                                observer irrelevant

   THE MARKER STILL NEVER HIDES ANYTHING. .os-reveal is a visible no-op; only
   .os-reveal--armed hides, and only this script adds it. If the file 404s or
   throws on line one, nothing is armed and the page is exactly as it would be
   without the effect. 51% of sessions here are the Instagram/Facebook webview
   and 6.37% throw a JS error.

   NO FLASH: elements already at or above the fold when this runs are marked
   shown and never armed, so a `defer` script cannot blink the top of the page
   out after it has painted.

   MOTION BUDGET (CSS side lives in os-tokens.css section 16)
     rise 20px · duration 500ms · easing var(--ease-out)
     stagger 60ms per element within a section, capped at 300ms
   ============================================================================ */

(function () {
  'use strict';

  if (window.__osLpRevealLoaded) return;
  window.__osLpRevealLoaded = true;

  var MARKER = '.os-reveal';
  var ARMED = 'os-reveal--armed';
  var SHOWN = 'is-revealed';
  var STATE = 'data-os-reveal-state';
  var GROUP = '[data-os-reveal-group]';

  var STAGGER_MS = 60;
  var STAGGER_CAP_MS = 300;
  var ROOT_MARGIN = '0px 0px 8% 0px';
  var LEAD = 1.08;              /* the same 8%, as a multiplier, for sweeps */
  var POLL_MS = 400;            /* layer 3 */
  var HARD_MS = 8000;           /* layer 4 */

  var observer = null;
  var pollId = null;

  /* THE ONLY WAY THIS FILE EVER LOOKS UP AN ELEMENT. No cached lists, no
     captured nodes — see the header. Anything armed and not yet shown, as the
     document actually is right now. */
  function pending() {
    try {
      return document.querySelectorAll('.' + ARMED + ':not(.' + SHOWN + ')');
    } catch (e) {
      return [];
    }
  }

  /* Keyed on the CLASS, not on the state attribute, because the class is what
     the CSS reads. A cloned node can arrive carrying state="shown" while still
     being visually hidden by the class; keying on the attribute would make
     this function refuse to fix exactly that node. */
  function reveal(el) {
    if (!el || el.classList.contains(SHOWN)) return;
    el.classList.add(SHOWN);
    el.setAttribute(STATE, 'shown');
    if (observer) {
      try { observer.unobserve(el); } catch (e) { /* not observed; fine */ }
    }
  }

  function stopPolling() {
    if (pollId !== null) {
      window.clearInterval(pollId);
      pollId = null;
    }
    try {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    } catch (e) { /* nothing to remove */ }
  }

  /* Reveal anything at or near the viewport. Negative tops (already scrolled
     past) are included — those must never be left hidden. */
  function sweep() {
    var nodes = pending();
    if (!nodes.length) { stopPolling(); return; }
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var limit = vh * LEAD;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i], top;
      try {
        top = el.getBoundingClientRect().top;
      } catch (e) {
        reveal(el);
        continue;
      }
      if (top < limit) reveal(el);
    }
    if (!pending().length) stopPolling();
  }

  var ticking = false;
  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    var run = function () {
      ticking = false;
      try { sweep(); } catch (e) { unarmEverything(); }
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  }

  /* Layer 4. Deliberately the dumbest thing in the file: no observer, no
     scroll, no position test, no shared state with anything above it. If the
     effect has not finished its job by now, the effect is not worth the risk
     of a single hidden word. */
  function unarmEverything() {
    try {
      var all = document.querySelectorAll('.' + ARMED);
      for (var i = 0; i < all.length; i++) {
        all[i].classList.remove(ARMED);
        all[i].classList.remove(SHOWN);
        all[i].style.removeProperty('--os-reveal-delay');
        all[i].setAttribute(STATE, 'shown');
      }
    } catch (e) { /* nothing more we can do */ }
    try { stopPolling(); } catch (e) {}
    try { if (observer) observer.disconnect(); } catch (e) {}
  }

  function onIntersect(entries) {
    try {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) reveal(entries[i].target);
      }
    } catch (e) {
      unarmEverything();
    }
  }

  function armOne(el, index) {
    var top;
    try {
      top = el.getBoundingClientRect().top;
    } catch (e) {
      el.setAttribute(STATE, 'shown');
      return false;
    }
    /* On screen already: never hide it. */
    if (top < window.innerHeight * 0.95) {
      el.setAttribute(STATE, 'shown');
      return false;
    }
    var delay = index * STAGGER_MS;
    if (delay > STAGGER_CAP_MS) delay = STAGGER_CAP_MS;
    el.style.setProperty('--os-reveal-delay', delay + 'ms');
    el.classList.add(ARMED);
    el.setAttribute(STATE, 'armed');
    if (observer) {
      try { observer.observe(el); } catch (e) { /* sweeps will get it */ }
    }
    return true;
  }

  function armGroup(group) {
    var nodes = group.querySelectorAll(MARKER);
    var armedIndex = 0;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].hasAttribute(STATE)) continue;
      if (armOne(nodes[i], armedIndex)) armedIndex++;
    }
  }

  function armWithin(scope) {
    var groups = scope.querySelectorAll(GROUP);
    var i;
    for (i = 0; i < groups.length; i++) armGroup(groups[i]);
    /* Marked but outside any group: still revealed, just with no delay. */
    var loose = scope.querySelectorAll(MARKER);
    for (i = 0; i < loose.length; i++) {
      if (!loose[i].hasAttribute(STATE)) armOne(loose[i], 0);
    }
  }

  function startPolling() {
    if (pollId !== null) return;
    try {
      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
    } catch (e) {
      window.addEventListener('scroll', onScrollOrResize);
      window.addEventListener('resize', onScrollOrResize);
    }
    pollId = window.setInterval(function () {
      try { sweep(); } catch (err) { unarmEverything(); }
    }, POLL_MS);
  }

  function init(scope) {
    try {
      armWithin(scope || document);
      if (pending().length) startPolling();
    } catch (e) {
      unarmEverything();
    }
  }

  try {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    try {
      observer = new IntersectionObserver(onIntersect, {
        root: null, rootMargin: ROOT_MARGIN, threshold: 0
      });
    } catch (e) {
      observer = null;   /* the sweeps do not need it */
    }

    init(document);

    /* Layer 4 is armed unconditionally, outside every other code path, and is
       never cancelled. Even if everything above this line is wrong. */
    window.setTimeout(unarmEverything, HARD_MS);

    document.addEventListener('shopify:section:load', function (e) {
      init(e && e.target ? e.target : document);
    });
  } catch (e) {
    unarmEverything();
  }
})();
